import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    Loader2,
    Mail,
    RefreshCw,
    Settings2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { evrakTalepMail } from "../lib/api";
import { openOutlookCompose, downloadEmlFile } from "../lib/sigortaMailto";

function EvrakChip({ label, yuklu, tone = "default" }) {
    const cls = yuklu
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
        : tone === "warn"
          ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
          : "border-slate-500/40 bg-slate-500/10 text-slate-400";
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>
            {yuklu ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {label}
        </span>
    );
}

export default function EvrakTalepMailPanel({ dosyaId, dosyaNo }) {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [onizle, setOnizle] = useState(null);
    const [konu, setKonu] = useState("");
    const [govde, setGovde] = useState("");
    const [alicilar, setAlicilar] = useState("");
    const [cc, setCc] = useState("");
    const [sablonOpen, setSablonOpen] = useState(false);
    const [sablon, setSablon] = useState(null);
    const [sablonKonu, setSablonKonu] = useState("");
    const [sablonGovde, setSablonGovde] = useState("");
    const [sablonTalep, setSablonTalep] = useState("");
    const [sablonCc, setSablonCc] = useState("");

    const load = useCallback(async () => {
        if (!dosyaId) return;
        setLoading(true);
        try {
            const o = await evrakTalepMail.onizle(dosyaId);
            if (!o?.success && o?.error) throw new Error(o.error);
            setOnizle(o);
            setKonu(o.konu || "");
            setGovde(o.govde || o.mail_plain_text || "");
            setAlicilar(o.alicilar || "");
            setCc(o.cc || "");
            try {
                const s = await evrakTalepMail.sablonGet();
                setSablon(s);
                setSablonKonu(s.konu_sablon || "");
                setSablonGovde(s.govde_sablon || "");
                setSablonTalep(s.varsayilan_talep_evraklari || "");
                setSablonCc(s.cc || "");
            } catch {
                /* şablon ayarı ikincil */
            }
        } catch (e) {
            const msg =
                e?.response?.status === 404
                    ? "API bulunamadı — sunucu yeniden başlatılıyor olabilir, sayfayı yenileyin."
                    : e?.response?.data?.error || e.message || "Evrak talep maili yüklenemedi";
            toast.error(msg);
            setOnizle(null);
        } finally {
            setLoading(false);
        }
    }, [dosyaId]);

    useEffect(() => {
        load();
    }, [load]);

    const ekteEvraklar = useMemo(() => {
        return (onizle?.evrak_durum || []).filter((e) => e.ekte);
    }, [onizle]);

    const yenidenUret = async () => {
        setBusy(true);
        try {
            const o = await evrakTalepMail.onizle(dosyaId);
            setOnizle(o);
            setKonu(o.konu || "");
            setGovde(o.govde || "");
            setAlicilar(o.alicilar || "");
            setCc(o.cc || "");
            toast.success("Şablondan yeniden üretildi");
        } catch (e) {
            toast.error(e?.response?.data?.error || "Yenileme başarısız");
        } finally {
            setBusy(false);
        }
    };

    const kaydetSablon = async () => {
        setBusy(true);
        try {
            const s = await evrakTalepMail.sablonKaydet({
                konu_sablon: sablonKonu,
                govde_sablon: sablonGovde,
                varsayilan_talep_evraklari: sablonTalep,
                cc: sablonCc,
            });
            setSablon(s);
            toast.success("Şablon kaydedildi");
            await yenidenUret();
        } catch {
            toast.error("Şablon kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const outlookAc = async () => {
        if (busy) return;
        if (!alicilar.trim()) {
            toast.error("Alıcı e-posta yok — sigorta şirketi kaydını kontrol edin");
            return;
        }
        setBusy(true);
        try {
            const data = await evrakTalepMail.hazirla({
                dosya_id: dosyaId,
                konu,
                govde,
                alicilar,
                cc,
            });
            openOutlookCompose(data.outlook || data);
            toast.success(
                "Outlook açılıyor — alıcı, konu ve metin aktarıldı. Vekalet/tutanak eklerini aşağıdan ekleyin.",
            );
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "Mail hazırlanamadı");
        } finally {
            setBusy(false);
        }
    };

    const emlIndir = async () => {
        if (busy) return;
        if (!alicilar.trim()) {
            toast.error("Alıcı e-posta yok");
            return;
        }
        setBusy(true);
        try {
            const data = await evrakTalepMail.hazirla({
                dosya_id: dosyaId,
                konu,
                govde,
                alicilar,
                cc,
            });
            const eml = data.eml || onizle?.eml;
            if (!eml) throw new Error("EML üretilemedi");
            const fname = `evrak_talep_${onizle?.dosya_no || dosyaNo || dosyaId}.eml`.replace(
                /[^\w.-]+/g,
                "_",
            );
            downloadEmlFile(eml, fname);
            toast.success("EML indirildi — dosyaya çift tıklayın, Outlook tam dolu açılır");
        } catch (e) {
            toast.error(e?.response?.data?.error || e.message || "EML indirilemedi");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-16 rounded-xl border border-white/10 bg-[#121A2F]">
                <Loader2 className="h-7 w-7 animate-spin text-[#D4AF37]" />
            </div>
        );
    }

    return (
        <section className="space-y-4">
            <div className="rounded-xl border border-sky-500/25 bg-gradient-to-br from-sky-500/8 to-[#121A2F] p-5">
                <div className="flex flex-wrap items-start gap-3">
                    <div className="rounded-lg bg-sky-500/15 p-2">
                        <Mail className="h-5 w-5 text-sky-300" />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <h2 className="font-display text-lg text-white">Evrak Talep Maili</h2>
                        <p className="text-sm text-slate-400 mt-1">
                            Dosyadan plaka, kaza tarihi ve evrak durumu alınır; sigorta şirketinin evrak
                            talep adresine Outlook taslağı açılır. Vekaletname ve tutanağı siz eklersiniz.
                        </p>
                        <div className="text-xs text-slate-500 font-mono mt-2">{dosyaNo}</div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={yenidenUret}
                            disabled={busy}
                            className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-300 inline-flex items-center gap-1"
                        >
                            <RefreshCw className="h-3.5 w-3.5" /> Yenile
                        </button>
                        <Link
                            to="/entity/sigorta-sirketleri"
                            className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-slate-400 inline-flex items-center gap-1"
                        >
                            Şirket adresleri <ExternalLink className="h-3 w-3" />
                        </Link>
                    </div>
                </div>

                {onizle?.uyarilar?.length > 0 ? (
                    <ul className="mt-4 space-y-1">
                        {onizle.uyarilar.map((u) => (
                            <li
                                key={u}
                                className="text-xs text-amber-200/90 flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2"
                            >
                                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                                {u}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4 space-y-3">
                    <h3 className="text-sm font-medium text-white">Dosya & şirket</h3>
                    <dl className="text-xs space-y-2">
                        <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Sigorta şirketi</dt>
                            <dd className="text-slate-200 text-right">
                                {onizle?.sigorta_sirketi?.ad || "—"}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Alıcı (evrak talep)</dt>
                            <dd className="text-sky-300 text-right break-all">{alicilar || "—"}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Araç</dt>
                            <dd className="text-slate-200 text-right">
                                {onizle?.context?.ARAC_OZET || "—"}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Plaka · kaza tarihi</dt>
                            <dd className="text-slate-200 text-right">
                                {onizle?.context?.PLAKA || "—"} · {onizle?.context?.KAZA_TARIHI || "—"}
                            </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                            <dt className="text-slate-500">Posta adresi</dt>
                            <dd className="text-slate-400 text-right whitespace-pre-wrap max-w-[240px]">
                                {onizle?.sigorta_sirketi?.mektup_adres || onizle?.context?.SIGORTA_ADRES || "—"}
                            </dd>
                        </div>
                    </dl>
                    <div>
                        <div className="text-[10px] uppercase text-slate-500 mb-2">Evrak durumu</div>
                        <div className="flex flex-wrap gap-1.5">
                            {ekteEvraklar.map((e) => (
                                <EvrakChip key={e.kod} label={e.label} yuklu={e.yuklu} tone="warn" />
                            ))}
                        </div>
                    </div>
                    {(onizle?.ek_evraklar || []).length > 0 ? (
                        <div className="pt-2 border-t border-white/5">
                            <div className="text-[10px] uppercase text-slate-500 mb-2">
                                Outlook&apos;a eklenecek dosyalar
                            </div>
                            <ul className="space-y-1">
                                {(onizle.ek_evraklar || []).map((ev) => (
                                    <li key={ev.evrak_id}>
                                        <a
                                            href={ev.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-sky-300 hover:underline"
                                        >
                                            {ev.label} — indir & ekle
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>

                <div className="rounded-xl border border-white/10 bg-[#121A2F]/80 p-4 space-y-3">
                    <label className="block text-[10px] uppercase text-slate-500">Alıcı</label>
                    <input
                        value={alicilar}
                        onChange={(e) => setAlicilar(e.target.value)}
                        className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                        placeholder="eposta@sigorta.com.tr"
                    />
                    <label className="block text-[10px] uppercase text-slate-500">CC</label>
                    <input
                        value={cc}
                        onChange={(e) => setCc(e.target.value)}
                        className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                    />
                    <label className="block text-[10px] uppercase text-slate-500">Konu</label>
                    <input
                        value={konu}
                        onChange={(e) => setKonu(e.target.value)}
                        className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                    />
                    <label className="block text-[10px] uppercase text-slate-500">Mail metni</label>
                    <textarea
                        value={govde}
                        onChange={(e) => setGovde(e.target.value)}
                        rows={12}
                        className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-slate-200 font-mono leading-relaxed"
                    />
                    <button
                        type="button"
                        onClick={outlookAc}
                        disabled={busy}
                        className="w-full py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {busy ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Mail className="h-4 w-4" />
                        )}
                        Outlook&apos;ta aç (dolu taslak)
                    </button>
                    <button
                        type="button"
                        onClick={emlIndir}
                        disabled={busy}
                        className="w-full py-2 rounded-lg border border-sky-500/40 text-sky-200 text-xs disabled:opacity-50"
                    >
                        EML indir — Outlook&apos;ta %100 dolu açılır
                    </button>
                    <p className="text-[10px] text-slate-500">
                        ms-outlook bazen gövdeyi kısaltabilir; EML yöntemi tüm alanları garanti eder.
                    </p>
                </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-[#121A2F]/60 overflow-hidden">
                <button
                    type="button"
                    onClick={() => setSablonOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm text-slate-300 hover:bg-white/5"
                >
                    <span className="inline-flex items-center gap-2">
                        <Settings2 className="h-4 w-4 text-[#D4AF37]" />
                        Şablon ayarları (tüm dosyalar için)
                    </span>
                    {sablonOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {sablonOpen ? (
                    <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-4">
                        <p className="text-xs text-slate-500">
                            Placeholder:{" "}
                            {(sablon?.placeholderlar || []).slice(0, 8).join(", ")}
                            … — örn. {"{{PLAKA}}"}, {"{{KAZA_TARIHI}}"}, {"{{TALEP_EDILEN_EVRAKLAR}}"}
                        </p>
                        <input
                            value={sablonKonu}
                            onChange={(e) => setSablonKonu(e.target.value)}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                            placeholder="Konu şablonu"
                        />
                        <textarea
                            value={sablonGovde}
                            onChange={(e) => setSablonGovde(e.target.value)}
                            rows={10}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-xs text-slate-300 font-mono"
                            placeholder="Gövde şablonu"
                        />
                        <textarea
                            value={sablonTalep}
                            onChange={(e) => setSablonTalep(e.target.value)}
                            rows={5}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-xs text-slate-300 font-mono"
                            placeholder="Varsayılan talep edilen evrak maddeleri"
                        />
                        <input
                            value={sablonCc}
                            onChange={(e) => setSablonCc(e.target.value)}
                            className="w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm text-white"
                            placeholder="Varsayılan CC"
                        />
                        <button
                            type="button"
                            onClick={kaydetSablon}
                            disabled={busy}
                            className="px-4 py-2 rounded-lg bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#F3E5AB] text-sm"
                        >
                            Şablonu kaydet
                        </button>
                    </div>
                ) : null}
            </div>
        </section>
    );
}
